import React from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { has, isEmpty } from "lodash";
import PropTypes from "prop-types";
import classNames from "classnames";
import { hasProperty } from "helpers/object";
import ReactTooltip from "react-tooltip";
import Translate from "helpers/Translate";

import "./FileInfo.css";

export const FileInfo = ({ isHidden }) => {
  const selectedFile = useSelector(state => state.file.getFile);

  return (
    <div
      className={classNames("tab-content tab-content-three selected", {
        hide: isHidden
      })}
    >
      <h2 className="title my-20">{Translate({ id: "file_details" })} :</h2>
      <ul className="file-info">
        {/* EisenLens Filename */}
        <li className="mb-20">
          <strong className="mb-5">
            {Translate({ id: "eisenlens_filename" })}:
          </strong>
          <span className="value ml-5">
            {hasProperty(
              () => selectedFile.eisenlens_filename,
              Translate({ id: "not_available" })
            )}
          </span>
        </li>

        {/* Relative Path */}
        <li className="mb-20">
          <strong className="mb-5">
            {Translate({ id: "relative_path" })} :
          </strong>
          <span className="value ml-5">
            {hasProperty(
              () => selectedFile.clean_path,
              Translate({ id: "not_available" })
            )}
          </span>
        </li>

        {/* Local Path */}
        {!isEmpty(selectedFile.local_path) && (
          <li className="mb-20">
            <strong className="mb-5">
              {Translate({ id: "local_path" })} :
            </strong>
            <span className="value ml-5">
              {hasProperty(
                () => selectedFile.local_path,
                Translate({ id: "not_available" })
              )}
            </span>
          </li>
        )}

        {/* Cloud Path */}
        {!isEmpty(selectedFile.remote_cloud_path) && (
          <li className="mb-20">
            <strong className="mb-5">
              {Translate({ id: "cloud_path" })} :
            </strong>
            <span className="value ml-5">
              {hasProperty(
                () => selectedFile.remote_cloud_path,
                Translate({ id: "not_available" })
              )}
            </span>
          </li>
        )}

        {/* Adapter Path */}
        {!isEmpty(selectedFile.adapter_path) && (
          <li className="mb-20">
            <strong className="mb-5">
              {Translate({ id: "adapter_path" })} :
            </strong>
            <span className="value ml-5">
              {hasProperty(
                () => selectedFile.adapter_path,
                Translate({ id: "not_available" })
              )}
            </span>
          </li>
        )}

        {/* Status */}
        <li className="mb-20">
          <strong className="mb-5">{Translate({ id: "status" })} :</strong>
          <span className="value ml-5">
            {hasProperty(
              () => selectedFile.status.toUpperCase(),
              Translate({ id: "not_available" })
            )}
          </span>
        </li>

        {/* Assigned To */}
        <li className="mb-20">
          <strong className="mb-5">{Translate({ id: "assigned_to" })} :</strong>
          <span className="value ml-5">
            {hasProperty(
              () =>
                selectedFile.assigned_users.map(
                  user => `- ${user.fullname} (${user.email})`
                ),
              Translate({ id: "not_available" })
            )}
          </span>
        </li>

        {/* Size */}
        <li className="mb-20">
          <strong className="mb-5">{Translate({ id: "size" })} :</strong>
          <span className="value ml-5">
            <span className="kb mr-5">
              {hasProperty(
                () => `${(selectedFile.file_size / 1000).toFixed(2)} KB`,
                Translate({ id: "not_available" })
              )}
            </span>
            <span className="mr-5">{Translate({ id: "or" })}</span>
            <span className="mb">
              {hasProperty(
                () => `${(selectedFile.file_size / 1000 / 1000).toFixed(2)} MB`,
                Translate({ id: "not_available" })
              )}
            </span>
          </span>
        </li>

        {/* Created At */}
        <li className="mb-20">
          <strong className="mb-5">{Translate({ id: "created" })}:</strong>
          <span className="value ml-5">
            {hasProperty(
              () =>
                moment(selectedFile.created_at).format("MMMM Do YYYY, H:mm"),
              "--"
            )}
          </span>
        </li>

        {/* Updated At */}
        <li className="mb-20">
          <strong className="mb-5">{Translate({ id: "updated" })}:</strong>
          <span className="value ml-5">
            {hasProperty(
              () =>
                moment(selectedFile.updated_at).format("MMMM Do YYYY, H:mm"),
              "--"
            )}
          </span>
        </li>

        {/* Characters*/}
        <li className="mb-20">
          <strong className="mb-5">
            {Translate({ id: "characters_entered" })}:
          </strong>
          <span className="value ml-5">
            {hasProperty(() => selectedFile.characters_entered, 0)}
          </span>
        </li>

        {/* Last Submitted */}
        {has(selectedFile, "last_submitted") &&
          !isEmpty(selectedFile.last_submitted) && (
            <li className="mb-20">
              <ReactTooltip />
              <strong className="mb-5">
                {Translate({ id: "submitted" })}:
              </strong>
              <span
                className="value ml-5"
                data-tip={hasProperty(
                  () => selectedFile.last_submitted.user.email
                )}
              >
                {hasProperty(
                  () => selectedFile.last_submitted.user.fullname,
                  Translate({ id: "not_available" })
                )}{" "}
              </span>
              <span
                className="timestamp"
                title={hasProperty(
                  () =>
                    moment(selectedFile.last_submitted.submitted_at).format(
                      "MMMM Do YYYY, H:mm"
                    ),
                  "..."
                )}
              >
                {hasProperty(
                  () =>
                    moment(selectedFile.last_submitted.submitted_at).format(
                      "MMMM Do YYYY"
                    ),
                  "..."
                )}
              </span>
            </li>
          )}

        {/* Last Approved */}
        {has(selectedFile, "last_approved") &&
          !isEmpty(selectedFile.last_approved) && (
            <li className="mb-20">
              <strong className="mb-5">
                {Translate({ id: "last_approved" })}:
              </strong>
              <span
                className="value ml-5"
                data-tip={hasProperty(
                  () => selectedFile.last_approved.user.email
                )}
              >
                {hasProperty(
                  () => selectedFile.last_approved.user.fullname,
                  Translate({ id: "not_available" })
                )}
              </span>
              <span
                className="timestamp"
                title={hasProperty(
                  () =>
                    moment(selectedFile.last_approved.reviewed_at).format(
                      "MMMM Do YYYY, H:mm"
                    ),
                  "..."
                )}
              >
                {hasProperty(
                  () =>
                    moment(selectedFile.last_approved.reviewed_at).format(
                      "MMMM Do YYYY"
                    ),
                  "..."
                )}
              </span>
            </li>
          )}

        {/* Last Rejected */}
        {has(selectedFile, "last_rejected") &&
          !isEmpty(selectedFile.last_rejected) && (
            <li className="mb-20">
              <strong className="mb-5">
                {Translate({ id: "last_rejected" })}:
              </strong>
              <span
                className="value ml-5"
                data-tip={hasProperty(
                  () => selectedFile.last_rejected.user.email
                )}
              >
                {hasProperty(
                  () => selectedFile.last_rejected.user.fullname,
                  Translate({ id: "not_available" })
                )}
              </span>
              <span
                className="timestamp"
                title={hasProperty(
                  () =>
                    moment(selectedFile.last_rejected.rejected_at).format(
                      "MMMM Do YYYY, H:mm"
                    ),
                  "..."
                )}
              >
                {hasProperty(
                  () =>
                    moment(selectedFile.last_rejected.rejected_at).format(
                      "MMMM Do YYYY"
                    ),
                  "..."
                )}
              </span>
            </li>
          )}

        {!isEmpty(selectedFile, "prediction") && (
          <li className="mb-20">
            <strong className="mb-5">
              {Translate({ id: "predictions" })}:
            </strong>
            {selectedFile?.prediction?.predictions?.length
              ? selectedFile.prediction.predictions.map((item, idx) => (
                  <div key={idx} className="value ml-5 mb-10">
                    <div className="mb-5">
                      {Translate({ id: "page" })}: {idx + 1}
                    </div>
                    {/* Content */}
                    <div className="ml-5">
                      {item.prediction.toUpperCase()}

                      {/* Other Predictions */}
                      <div className="ml-10 mt-5">
                        <strong className="mb-5">
                          {Translate({ id: "other_predictions" })}:
                        </strong>
                        {has(item, "probability") &&
                          Object.keys(item.probability).map((keyName, i) => (
                            <div key={i} className="value ml-5 mt-5">
                              <span>{keyName} :</span>
                              <span className="ml-5">
                                {item.probability[keyName]}
                              </span>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                ))
              : "--"}

            {!isEmpty(selectedFile.prediction.error) ? (
              <div className="value">{selectedFile.prediction.error}</div>
            ) : null}
          </li>
        )}
      </ul>
      <hr />

      {/* History */}
      <h2 className="title my-20">
        {Translate({ id: "history" })}: (
        {hasProperty(() => selectedFile?.history?.length, 0)})
      </h2>
      {selectedFile.history.length === 0 ? (
        <p className="help">{Translate({ id: "no_history_found" })}</p>
      ) : (
        <ul className="history-timeline">
          {has(selectedFile, "history") &&
            !isEmpty(selectedFile.history) &&
            selectedFile.history.map((history, idx) => (
              <li key={idx} className="mb-20">
                <div className="timestamp">
                  {moment(history.created_at).format("MMMM Do YYYY, H:mm")}
                </div>
                <div className="my-20">
                  <strong className="mb-5">
                    {Translate({ id: "action_by" })} :
                  </strong>{" "}
                  {hasProperty(() => history.user.fullname)} (
                  <i>{hasProperty(() => history.user.email)}</i>)
                </div>
                <div className="my-20">
                  <strong className="mb-5">
                    {Translate({ id: "action_taken" })} :
                  </strong>{" "}
                  {Translate({ id: history.status, uppercase: true })}
                </div>
                <div className="my-20">
                  <strong className="mb-5">
                    {Translate({ id: "comment" })} :
                  </strong>
                  <p>{history.comment || "--"}</p>
                </div>
              </li>
            ))}
        </ul>
      )}
      <div className="my-50">&nbsp;</div>
    </div>
  );
};

FileInfo.propType = {
  isHidden: PropTypes.bool
};

export default FileInfo;
